import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { ActiveMeters, Simulation } from "../edp-consumptions.models";
import { Subject, Subscription } from "rxjs";
import { select, Store } from "@ngrx/store";
import { getInvoices, getSimulation, getUploadReading, getActiveMetersElectricity, getActiveMetersGas } from "@edp-store/selectors/dashboard.selectors";
import { IPropertyType } from "@edp-components/edp-contract/edp-contract.model";
import { IAppState } from "@edp-store/state/app.state";
import { takeUntil, withLatestFrom, take } from "rxjs/operators";
import { GetActiveMeters, GetSimulation, SetActiveMetersElectricity, SetActiveMetersGas } from "@edp-store/actions/dashboard.actions";
import moment from "moment";
import { IInvoice } from "@edp-models/invoices.model";
import { GetConsumptions } from "@edp-store/actions/consumption.actions";
import { getConsumptions } from "@edp-store/selectors/consumption.selectors";
import { IConsumptions } from "@edp-components/edp-card-widgets/edp-widget-conta-certa/edp-conta-certa.model";

@Component({
  selector: "edp-widget-consumptions-simulation-conta-certa",
  templateUrl: "./edp-widget-consumptions-simulation-conta-certa.component.html",
  styleUrls: ["./edp-widget-consumptions-simulation-conta-certa.component.scss"]
})
export class EdpWidgetConsumptionsSimulationContaCertaComponent implements OnInit, OnDestroy {

    @Input() contract: Contract;
    @Input() activeTab: string;
    @Input() hiddenTabActiveMetersSubmitted: ActiveMeters;
    @Output() reloadWidget: EventEmitter<string> = new EventEmitter();

    private onDestroy$ = new Subject<boolean>();
    public invoices$ = this.store.pipe(select(getInvoices));
    public simulation$ = this.store.pipe(select(getSimulation));

    public activeTabActiveMeters: ActiveMeters = null;
    public isInvoicesAverage: boolean;
    public successUploadReading: boolean;
    public type: IPropertyType;
    public consumptionsObject: IConsumptions = null;
    public valueContaCerta = 0;
    public valueContaCertaMedia = 0;
    public valueContaCertaAcerto = "0";
    public contaCertaPercentage = 0;
    public valueContaCertaAcertoFinal: string;
    public labelToPayOrRecieve: string;
    public offlineReading = false;
    private tabSubscription: Subscription;

    constructor(
        private store: Store<IAppState>
    ) {

    }

    ngOnInit() {
        this.simulation$.pipe(takeUntil(this.onDestroy$), withLatestFrom(this.invoices$))
            .subscribe(([simulation, invoice]) => {
                if (simulation) {
                    this.store.dispatch(new GetActiveMeters(this.contract, this.activeTab));
                    this.valueContaCertaAcerto = simulation.simulationValue;
                }
                if (simulation && invoice.invoices) {
                    this.isInvoicesAverage = this.getInvoicesAverage(simulation, invoice.invoices);
                }
            });

        this.clearActiveTabActiveMeters(this.activeTab);

        this.store.pipe(takeUntil(this.onDestroy$), select(getUploadReading)).subscribe((uploadReading) => {
            if (uploadReading) {
                if (uploadReading.success) {
                    this.store.dispatch(new GetSimulation(this.contract, false));
                    this.store.dispatch(new GetConsumptions(this.contract));
                    this.store.pipe(
                        select(getConsumptions),
                        take(1)
                    ).subscribe(consumptions => {
                        let missingMonths = 0;
                        this.valueContaCerta = this.contract.ContaCertaValue ? parseInt(this.contract.ContaCertaValue, 0) : 0;
                        if (consumptions) {
                            this.consumptionsObject = consumptions;
                            missingMonths = this.consumptionsObject.endContaCerta
                                ? moment(this.consumptionsObject.endContaCerta).subtract(1, "months").diff(moment(), "months") : null;
                        }

                        this.valueContaCertaAcerto = (parseFloat(this.valueContaCertaAcerto) -
                                                        (this.valueContaCerta * missingMonths)).toFixed(2);

                        this.labelToPayOrRecieve = this.valueContaCertaAcerto > "0" ? "pt.edp.edponline.readings.conta.certa.to.pay" :
                            "pt.edp.edponline.readings.conta.certa.to.recieve";
                        this.valueContaCertaAcerto = Math.abs(parseFloat(this.valueContaCertaAcerto)).toString();
                        this.valueContaCertaAcertoFinal = this.valueContaCertaAcerto;

                        this.contaCertaPercentage = (parseFloat(this.valueContaCertaAcerto) - this.valueContaCerta)
                        / parseFloat(this.valueContaCertaAcerto);

                    });
                } else {
                    if (uploadReading.registersError) {
                        this.reloadWidget.emit(this.activeTab);
                    } else {
                        this.offlineReading = true;
                    }
                }
            }
        });

        if (this.hiddenTabActiveMetersSubmitted) {
            this.hasSubmittedActiveMeters();
        }


        this.type = this.contract.getTypeProperties(this.contract.getType());
    }

    private clearActiveTabActiveMeters = (activeTab: string) => {
        if (this.tabSubscription) {
            this.tabSubscription.unsubscribe();
        }
        if (activeTab === "E") {
            this.store.dispatch(new SetActiveMetersElectricity(null));
            this.tabSubscription = this.store.pipe(
                takeUntil(this.onDestroy$),
                select(getActiveMetersElectricity)).subscribe((meters: ActiveMeters[]) => {
                    if (!!meters && meters.length) {
                        this.activeTabActiveMeters = meters[0];
                    }
                });
        } else if (activeTab === "G") {
            this.store.dispatch(new SetActiveMetersGas(null));
            this.tabSubscription = this.store.pipe(
                takeUntil(this.onDestroy$),
                select(getActiveMetersGas)).subscribe((meters: ActiveMeters[]) => {
                    if (!!meters && meters.length) {
                        this.activeTabActiveMeters = meters[0];
                    }
                });
        }
    }

    private hasSubmittedActiveMeters = (): ActiveMeters => {
        let hasSubmittedActiveMeters: ActiveMeters = null;

        if (this.contract.getType() === "dual" && this.hiddenTabActiveMetersSubmitted.lastInserted) {
            const currentDate = moment().startOf("day");
            const lastInsertedDate = moment(this.hiddenTabActiveMetersSubmitted.lastInserted.date, "YYYY-MM-DD");

            if (moment(currentDate).isSame(lastInsertedDate)) {
                hasSubmittedActiveMeters = this.hiddenTabActiveMetersSubmitted;
            }
        }
        return hasSubmittedActiveMeters;
    }

    private getInvoicesAverage = (simulation: Simulation, invoices: IInvoice[]): boolean => {
        const simulationValue: number = Number(simulation.simulationValue.replace(",", ".")) || 0;

        const invoicesAverage: number = this.getAverage(invoices);
        const rangeMaxAverage: number = invoicesAverage + (invoicesAverage * 0.2);
        const rangeMinAverage: number = invoicesAverage - (invoicesAverage * 0.2);

        return simulationValue > rangeMinAverage && simulationValue < rangeMaxAverage;
    }

    private getAverage = (invoices: IInvoice[]): number => {
        const invoicesValue: number[] = invoices.map(invoice => Number(invoice.invoiceValueEur.replace(",", ".")));
        return invoicesValue.reduce((total: number, score: number) => total + score, 0) / invoicesValue.length;
    }

    public ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.unsubscribe();
    }
}
